import {
  AUTOMATION_ACTION_KEY,
  AUTOMATION_CONDITION_KEY,
} from "../../../../slices/AutomationSlice";
import {
  AutomationItem,
  AutomationLookupOptions,
  AutomationOptionsKey,
} from "./automation.constants";
import {
  getActionConfig,
  getConditionConfig,
  getDateValueDays,
  getDateValueDirection,
  getTemplateEmailAddress,
  renderActionSummary,
  renderConditionSummary,
  resolveAutomationValueLabel,
  resolveOptionLabel,
} from "./automation.helpers";

const getOptionLabel = (
  options: AutomationLookupOptions,
  optionsKey: AutomationOptionsKey,
  value: AutomationItem["conditionValue"] | AutomationItem["actionValue"],
  fallback = "",
) => {
  if (typeof value === "string" || value === undefined) {
    return resolveOptionLabel(options, optionsKey, value, fallback);
  }

  return resolveAutomationValueLabel(options, optionsKey, value, fallback);
};

/** A piece of the summary sentence: plain words, the subtask name, or a picked value. */
export type AutomationSummaryPart = {
  text: string;
  kind?: "name" | "value";
};

const plain = (text: string): AutomationSummaryPart => ({ text });
const value = (text: string): AutomationSummaryPart => ({
  text,
  kind: "value",
});

const getAutomationEntityParts = (
  automation: AutomationItem,
  workName: string,
): AutomationSummaryPart[] => {
  if (automation.scope === "subtask") {
    return [
      plain("subtask"),
      {
        text: `"${automation.subtaskTitle ?? "Selected subtask"}"`,
        kind: "name",
      },
    ];
  }

  return [plain(`work item ${automation.workItemTitle ?? workName}`)];
};

const getAutomationConditionParts = (
  automation: AutomationItem,
  options: AutomationLookupOptions,
): AutomationSummaryPart[] => {
  switch (automation.conditionKey) {
    case AUTOMATION_CONDITION_KEY.WORK_STATUS:
      return [
        plain("has a status of"),
        value(
          getOptionLabel(
            options,
            "workStatuses",
            automation.conditionValue,
            "selected status",
          ),
        ),
      ];

    case AUTOMATION_CONDITION_KEY.REACHED_DUE_DATE:
      return [plain("due date is reached")];

    case AUTOMATION_CONDITION_KEY.WORK_REACHES_DATE:
      return [
        plain("reaches"),
        value(
          `${
            getDateValueDays(automation.conditionValue) || "selected date"
          } ${getOptionLabel(
            options,
            "dateRules",
            getDateValueDirection(automation.conditionValue),
            "selected timing",
          )}`,
        ),
      ];

    case AUTOMATION_CONDITION_KEY.CURRENT_SUBTASK_STATUS:
      return [
        plain("has a status of"),
        value(
          getOptionLabel(
            options,
            "subtaskStatuses",
            automation.conditionValue,
            "selected status",
          ),
        ),
      ];

    case AUTOMATION_CONDITION_KEY.PREVIOUS_SUBTASK_STATUS:
      return [
        plain("previous subtask has a status of"),
        value(
          getOptionLabel(
            options,
            "subtaskStatuses",
            automation.conditionValue,
            "selected status",
          ),
        ),
      ];

    case AUTOMATION_CONDITION_KEY.ALL_SUBTASKS_COMPLETED:
      return [plain("all previous subtasks are completed")];

    case AUTOMATION_CONDITION_KEY.SUBTASK_REACHED_DUE_DATE:
      return [plain("has reached its due date")];

    default: {
      const condition = getConditionConfig(automation.conditionKey);

      return condition
        ? [
            plain(
              renderConditionSummary(
                condition,
                options,
                automation.conditionValue,
                undefined,
              ),
            ),
          ]
        : [];
    }
  }
};

const getAutomationActionParts = (
  automation: AutomationItem,
  options: AutomationLookupOptions,
): AutomationSummaryPart[] => {
  switch (automation.actionKey) {
    case AUTOMATION_ACTION_KEY.ASSIGNEE:
      return [
        plain("add"),
        value(
          getOptionLabel(
            options,
            "users",
            automation.actionValue,
            "selected team member",
          ),
        ),
        plain(
          `as a team member to this ${
            automation.scope === "subtask" ? "subtask" : "work item"
          }`,
        ),
      ];

    case AUTOMATION_ACTION_KEY.STATUS:
      return [
        plain("change the status to"),
        value(
          getOptionLabel(
            options,
            "workStatuses",
            automation.actionValue,
            "selected status",
          ),
        ),
      ];

    case AUTOMATION_ACTION_KEY.SEND_EMAIL: {
      const emailTemplate = getOptionLabel(
        options,
        "emailTemplates",
        automation.actionValue,
        "selected email template",
      );
      const emailAddress = getTemplateEmailAddress(automation.actionValue);

      return [
        plain("send client email"),
        value(`"${emailTemplate}"`),
        ...(emailAddress ? [plain("to"), value(emailAddress)] : []),
      ];
    }

    case AUTOMATION_ACTION_KEY.SEND_NOTIFICATION:
      return [
        plain("send"),
        value(
          getOptionLabel(
            options,
            "teamMembers",
            automation.actionValue,
            "selected team member",
          ),
        ),
        plain("a notification"),
      ];

    case AUTOMATION_ACTION_KEY.CHANGE_STATUS:
      return [
        plain("change the current subtask status to"),
        value(
          getOptionLabel(
            options,
            "subtaskStatuses",
            automation.actionValue,
            "selected status",
          ),
        ),
      ];

    default: {
      const action = getActionConfig(automation.actionKey);

      return action
        ? [plain(renderActionSummary(action, options, automation.actionValue))]
        : [];
    }
  }
};

/**
 * The automation sentence in pieces, e.g. When subtask "Partner Review"
 * previous subtask has a status of **Waiting**, change the current subtask
 * status to **In Progress** (used by the ⚡ tooltip, Roadmap 1801).
 */
export const getAutomationSummaryParts = ({
  automation,
  workName,
  options,
}: {
  automation: AutomationItem;
  workName: string;
  options: AutomationLookupOptions;
}): AutomationSummaryPart[] => {
  const conditionParts = getAutomationConditionParts(automation, options);
  const actionParts = getAutomationActionParts(automation, options);

  return [
    plain("When"),
    ...getAutomationEntityParts(automation, workName),
    ...conditionParts,
    ...(automation.scope === "subtask" && conditionParts.length
      ? [plain(",")]
      : []),
    ...actionParts,
  ].filter((part) => part.text);
};

/** Space before a part, except before punctuation. */
export const needsSpaceBefore = (part: AutomationSummaryPart, index: number) =>
  index > 0 && !/^[,.]/.test(part.text);

export const getAutomationSummaryText = (args: {
  automation: AutomationItem;
  workName: string;
  options: AutomationLookupOptions;
}): string =>
  getAutomationSummaryParts(args)
    .map((part, index) =>
      needsSpaceBefore(part, index) ? ` ${part.text}` : part.text,
    )
    .join("");
